import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import { queryClient } from "@shared/api/queries/queryClient";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { toast } from "@shared/ui/use-toast";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { useNavigate } from "react-router-dom";
import { axIncidentApi } from "./api";

export const useCreateIncident = () => {
  return useSmartMutation(
    (payload: any) =>
      axIncidentApi.creatIncident({
        ...payload,
        remote_utility_id: getRemoteUtilityId(),
      }),
    {
      onSuccess: () => {
        toast({
          title: "Incident created successfully!",
          variant: "default",
        });

        const key = QueryKeyFactory.module.ax.incidents
          .incidentsList()
          .slice(0, 4);

        queryClient.invalidateQueries({
          queryKey: key,
          exact: false,
        });
      },
      onError: (error: Error) => {
        toast({
          title: `Failed to create an incident: ${error.message}`,
          variant: "destructive",
        });
      },
    }
  );
};

export const useUpdateIncident = (id: string, isDetail?: boolean) => {
  const navigate = useNavigate();
  return useSmartMutation(
    (payload: any) =>
      axIncidentApi.updateIncident(id, {
        ...payload,
        remote_utility_id: getRemoteUtilityId(),
      }),
    {
      onSuccess: () => {
        toast({
          title: "Incident updated successfully!",
          variant: "default",
        });

        const key = QueryKeyFactory.module.ax.incidents
          .incidentsList()
          .slice(0, 4);
        const incidentDetail = QueryKeyFactory.module.ax.incidents
          .incidentDetail()
          .slice(0, 4);

        queryClient.refetchQueries({
          queryKey: incidentDetail,
        });

        const listKey = QueryKeyFactory.module.ax.incidents
          .incidentsList()
          .slice(0, 4);

        queryClient.invalidateQueries({
          queryKey: listKey,
          exact: false,
        });

        queryClient.refetchQueries({
          queryKey: key,
          exact: false,
        });
      },
      onError: (error: Error) => {
        toast({
          title: `Failed to update an incident: ${error.message}`,
          variant: "destructive",
        });
      },
    }
  );
};

export const useGetIncidents = (queryParams: Record<string, any>) => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.incidents.incidentsList(queryParams),
    () => axIncidentApi.getIncidents(queryParams)
  );
};

export const useGetAssets = (queryParams: Record<string, any>) => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.incidents.assetsList(queryParams),
    () => axIncidentApi.getAssetList(queryParams)
  );
};

export const useIncidentTypeOptions = () => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.incidents.incidentTypeOptions(),
    () => axIncidentApi.getIncidentTypeOptions()
  );
};

export const useIncidentDetail = (id: string) => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.incidents.incidentDetail(id),
    () => axIncidentApi.getIncidentDetail(id),
    {
      enabled: !!id,
    }
  );
};

export const useUpdateDocuments = (id: string) => {
  return useSmartMutation(
    (documents: FormData) => axIncidentApi.updateIncident(id, documents),
    {
      onSuccess: () => {
        toast({
          title: "Incident updated successfully!",
          variant: "default",
        });

        const incidentDetail = QueryKeyFactory.module.ax.incidents
          .incidentDetail()
          .slice(0, 4);

        queryClient.refetchQueries({
          queryKey: incidentDetail,
          exact: false,
        });
      },
      onError: (error: Error) => {
        toast({
          title: `Failed to update an incident: ${error.message}`,
          variant: "destructive",
        });
      },
    }
  );
};

export const useIgnoreIncident = (isDetail?: boolean) => {
  return useSmartMutation(
    ({ id, payload }: { id: string; payload: any }) =>
      axIncidentApi.updateIncident(id, {
        ...payload,
        remote_utility_id: getRemoteUtilityId(),
      }),
    {
      onSuccess: () => {
        toast({
          title: "Incident Ignored successfully!",
          variant: "default",
        });

        const key = QueryKeyFactory.module.ax.incidents
          .incidentsList()
          .slice(0, 4);

        const incidentDetail = QueryKeyFactory.module.ax.incidents
          .incidentDetail()
          .slice(0, 4);

        queryClient.refetchQueries({
          queryKey: incidentDetail,
        });

        queryClient.refetchQueries({
          queryKey: key,
          exact: false,
        });
      },
      onError: (error: Error) => {
        toast({
          title: `Failed to Ignore an incident: ${error.message}`,
          variant: "destructive",
        });
      },
    }
  );
};

export const useIncidentDashboard = () => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.incidents.incidentDashboard(),
    () => axIncidentApi.getIncidentDashboard()
  );
};
